/**
 * Read-only tool-run inspector over durable attempt events.
 * Does not claim work, buy vendors, or mutate tool_runs.
 */
import { redactAttomSecrets } from "./attom-avm.js";
import { FAILURE_CLASSES, isRetryableFailure, type FailureClass } from "./failures.js";
import type { PipelineToolEvent, PipelineToolEventKind } from "./pipeline-tool-attempt.js";
import { readPipelineToolUsage, type PipelineToolEvidenceRef, type PipelineToolUsage } from "./pipeline-handler.js";
import { PIPELINE_TOOL_STALE_MS } from "./pipeline-tool-status.js";

export const PIPELINE_TOOL_INSPECTOR_PHASES = [
  "started",
  "inputs",
  "rule_decision",
  "claim",
  "provider_work",
  "heartbeat",
  "evidence_saved",
  "validation",
  "retry",
  "completion",
  "failure"
] as const;
export type PipelineToolInspectorPhase = (typeof PIPELINE_TOOL_INSPECTOR_PHASES)[number];

export const PIPELINE_TOOL_INSPECTOR_TERMINAL_STATUSES = ["succeeded", "failed", "needs_review"] as const;

export type PipelineToolInspectorEvent = {
  kind: PipelineToolEventKind;
  phase: PipelineToolInspectorPhase;
  at: string;
  actor: string | null;
  message: string | null;
  details: Record<string, unknown> | null;
};

export type PipelineToolRunInspectorView = {
  id: string;
  toolId: string;
  toolVersion: string;
  policyVersion: string | null;
  inputVersion: string | null;
  status: string;
  failureClass: string | null;
  workItemId: string | null;
  attemptNo: number;
  operator: string;
  startedAt: string | null;
  completedAt: string | null;
  durationMs: number | null;
  leaseExpiresAt: string | null;
  stalled: boolean;
  currentReason: string | null;
  nextAutomaticAction: string | null;
  input: Record<string, unknown>;
  usage: PipelineToolUsage | null;
  evidence: PipelineToolEvidenceRef[];
  events: PipelineToolInspectorEvent[];
};

export type PipelineToolRunInspectorSource = {
  id: string;
  toolId: string;
  toolVersion: string;
  policyVersion?: string | null;
  inputVersion?: string | null;
  status: string;
  failureClass?: string | null;
  workItemId?: string | null;
  attemptNo?: number;
  operator?: string;
  input?: Record<string, unknown> | null;
  result?: Record<string, unknown> | null;
  evidence?: unknown;
  leaseExpiresAt?: string | null;
  startedAt?: string | null;
  completedAt?: string | null;
  createdAt?: string;
  events?: readonly PipelineToolEvent[];
};

export function isPipelineToolInspectorPhase(value: unknown): value is PipelineToolInspectorPhase {
  return typeof value === "string" && (PIPELINE_TOOL_INSPECTOR_PHASES as readonly string[]).includes(value);
}

export function isPipelineToolRunInspectorTerminal(status: string): boolean {
  return (PIPELINE_TOOL_INSPECTOR_TERMINAL_STATUSES as readonly string[]).includes(status);
}

export function pipelineToolEventPhase(event: PipelineToolEvent): PipelineToolInspectorPhase {
  const explicit = event.details && isPipelineToolInspectorPhase(event.details.phase) ? event.details.phase : null;
  if (explicit) return explicit;
  if (event.kind === "started") return "started";
  if (event.kind === "claimed") return "claim";
  if (event.kind === "completed") return "completion";
  if (event.kind === "failed") return "failure";
  if (event.details?.heartbeat === false) return "provider_work";
  return "heartbeat";
}

export function pipelineToolRunInspector(
  source: PipelineToolRunInspectorSource,
  now: string
): PipelineToolRunInspectorView {
  const result = source.result && typeof source.result === "object" && !Array.isArray(source.result)
    ? source.result
    : null;
  const events = (source.events ?? []).map((event) => ({
    kind: event.kind,
    phase: pipelineToolEventPhase(event),
    at: event.at,
    actor: redactActor(event.actor),
    message: event.message ?? null,
    details: event.details ? (redactAttomSecrets(event.details) as Record<string, unknown>) : null
  }));
  const startedAt = textOrNull(source.startedAt) ?? firstEventAt(events, "started") ?? textOrNull(source.createdAt);
  const completedAt = isPipelineToolRunInspectorTerminal(source.status)
    ? textOrNull(source.completedAt) ?? firstEventAt(events, "completed") ?? firstEventAt(events, "failed")
    : null;
  const leaseExpiresAt = textOrNull(source.leaseExpiresAt);
  const stalled = isStalled(source.status, leaseExpiresAt, events, now);
  const failureClass = textOrNull(source.failureClass) ?? failureClassFromResult(result);
  const evidence = redactEvidence(evidenceFromResult(result, source.evidence));
  const clock = completedAt ?? ((source.status === "running" || stalled) ? now : null);
  return {
    id: source.id,
    toolId: source.toolId,
    toolVersion: source.toolVersion,
    policyVersion: textOrNull(source.policyVersion),
    inputVersion: textOrNull(source.inputVersion),
    status: source.status,
    failureClass,
    workItemId: textOrNull(source.workItemId),
    attemptNo: Number.isFinite(source.attemptNo) ? Math.max(1, Math.floor(source.attemptNo!)) : 1,
    operator: source.operator?.trim() || "",
    startedAt,
    completedAt,
    durationMs: durationMs(startedAt, clock),
    leaseExpiresAt,
    stalled,
    currentReason: currentReason({ status: source.status, failureClass, stalled, leaseExpiresAt, events, result, now }),
    nextAutomaticAction: nextAutomaticAction({
      status: source.status,
      failureClass,
      stalled,
      retryable: retryableFailure(failureClass, result)
    }),
    input: redactAttomSecrets(source.input ?? {}) as Record<string, unknown>,
    usage: usageFromResult(result),
    evidence,
    events
  };
}

function isStalled(
  status: string,
  leaseExpiresAt: string | null,
  events: readonly PipelineToolInspectorEvent[],
  now: string
): boolean {
  if (status !== "running") return false;
  if (leaseExpiresAt && leaseExpiresAt <= now) return true;
  const lastAt = events.at(-1)?.at ?? null;
  if (!lastAt) return false;
  const last = Date.parse(lastAt);
  const clock = Date.parse(now);
  if (!Number.isFinite(last) || !Number.isFinite(clock)) return false;
  return clock - last >= PIPELINE_TOOL_STALE_MS;
}

function currentReason(input: {
  status: string;
  failureClass: string | null;
  stalled: boolean;
  leaseExpiresAt: string | null;
  events: readonly PipelineToolInspectorEvent[];
  result: Record<string, unknown> | null;
  now: string;
}): string | null {
  if (input.status === "succeeded") return null;
  if (input.stalled) {
    return input.leaseExpiresAt && input.leaseExpiresAt <= input.now
      ? "Lease expired; waiting for automatic reclaim."
      : "Heartbeat is stale; waiting for automatic reclaim.";
  }
  if (input.status === "queued") return "Queued; waiting to be claimed.";
  if (input.status === "running") {
    const last = [...input.events].reverse().find((event) => event.message) ?? input.events.at(-1);
    return last?.message || "Running; waiting for the next worker heartbeat.";
  }
  if (input.status === "needs_review") return input.failureClass || "Held for operator review.";
  const failed = [...input.events].reverse().find((event) => event.kind === "failed");
  const fromResult = failureMessageFromResult(input.result);
  return failed?.message || fromResult || input.failureClass;
}

function nextAutomaticAction(input: {
  status: string;
  failureClass: string | null;
  stalled: boolean;
  retryable: boolean;
}): string | null {
  if (input.status === "succeeded") return null;
  if (input.stalled) return "Recover the expired claim automatically. Inspector does not claim or repurchase.";
  if (input.status === "running") return "Wait for the next worker heartbeat.";
  if (input.status === "queued") return "Automatic claim when a worker is free. Inspector does not claim work.";
  if (input.status === "needs_review") return "Held for operator review.";
  if (input.status === "failed" && input.retryable) return "Automatic retry. Inspector does not replay paid work.";
  if (input.status === "failed") return "No automatic retry.";
  return null;
}

function retryableFailure(failureClass: string | null, result: Record<string, unknown> | null): boolean {
  const fromResult = result?.failure;
  if (fromResult && typeof fromResult === "object" && !Array.isArray(fromResult) && typeof (fromResult as { retryable?: unknown }).retryable === "boolean") {
    return (fromResult as { retryable: boolean }).retryable;
  }
  if (!failureClass) return false;
  if ((FAILURE_CLASSES as readonly string[]).includes(failureClass)) {
    return isRetryableFailure(failureClass as FailureClass);
  }
  return /timeout|rate_limited|unavailable|engine_error|browser_crashed|upstream/i.test(failureClass);
}

function usageFromResult(result: Record<string, unknown> | null): PipelineToolUsage | null {
  const usage = readPipelineToolUsage(result?.usage);
  if (!usage || Object.keys(usage).length === 0) return null;
  return usage;
}

function evidenceFromResult(result: Record<string, unknown> | null, sourceEvidence?: unknown): PipelineToolEvidenceRef[] {
  const evidence: PipelineToolEvidenceRef[] = [];
  const seen = new Set<string>();
  const add = (ref: PipelineToolEvidenceRef | null): void => {
    if (!ref) return;
    const key = `${ref.type}\0${ref.uri ?? ""}`;
    if (seen.has(key)) return;
    seen.add(key);
    evidence.push(ref);
  };
  addFromEvidenceList(sourceEvidence, add);
  if (!result) return evidence;
  addFromEvidenceList(result.evidence, add);
  addFromDocumentList(result.documents, add);
  addFromDocumentList(result.sourceDocuments, add);
  addFromDocumentList(result.source_documents, add);
  const artifacts = result.artifacts;
  if (artifacts && typeof artifacts === "object" && !Array.isArray(artifacts)) {
    const rec = artifacts as Record<string, unknown>;
    add(uriRef("screenshot", rec.screenshot_uri ?? rec.screenshotUri));
    add(uriRef("receipt", rec.receipt_uri ?? rec.receiptUri));
  }
  return evidence;
}

function addFromEvidenceList(value: unknown, add: (ref: PipelineToolEvidenceRef | null) => void): void {
  if (!Array.isArray(value)) return;
  for (const item of value) add(evidenceRefFromUnknown(item));
}

function addFromDocumentList(value: unknown, add: (ref: PipelineToolEvidenceRef | null) => void): void {
  if (!Array.isArray(value)) return;
  for (const item of value) {
    if (!item || typeof item !== "object" || Array.isArray(item)) continue;
    const rec = item as Record<string, unknown>;
    const type = textOrNull(rec.type) ?? textOrNull(rec.kind) ?? textOrNull(rec.document_type) ?? textOrNull(rec.documentType) ?? "document";
    add(uriRef(type, rec.uri ?? rec.artifact_uri ?? rec.artifactUri, rec.details));
    add(uriRef("text", rec.text_artifact_uri ?? rec.textArtifactUri));
  }
}

function evidenceRefFromUnknown(item: unknown, fallbackType = "artifact"): PipelineToolEvidenceRef | null {
  if (!item || typeof item !== "object" || Array.isArray(item)) return null;
  const rec = item as Record<string, unknown>;
  const type = textOrNull(rec.type) ?? textOrNull(rec.kind) ?? textOrNull(rec.document_type) ?? textOrNull(rec.documentType);
  const uri = textOrNull(rec.uri) ?? textOrNull(rec.artifact_uri) ?? textOrNull(rec.artifactUri);
  if (!type && !uri) return null;
  return {
    type: type ?? fallbackType,
    ...(uri ? { uri } : {}),
    ...(rec.details && typeof rec.details === "object" && !Array.isArray(rec.details)
      ? { details: rec.details as Record<string, unknown> }
      : {})
  };
}

function uriRef(type: string, uri: unknown, details?: unknown): PipelineToolEvidenceRef | null {
  const href = textOrNull(uri);
  if (!href) return null;
  return {
    type,
    uri: href,
    ...(details && typeof details === "object" && !Array.isArray(details)
      ? { details: details as Record<string, unknown> }
      : {})
  };
}

function redactEvidence(evidence: PipelineToolEvidenceRef[]): PipelineToolEvidenceRef[] {
  return evidence.map((item) => ({
    type: item.type,
    ...(item.uri ? { uri: item.uri } : {}),
    ...(item.details ? { details: redactAttomSecrets(item.details) as Record<string, unknown> } : {})
  }));
}

function failureClassFromResult(result: Record<string, unknown> | null): string | null {
  const failure = result?.failure;
  if (failure && typeof failure === "object" && !Array.isArray(failure)) {
    return textOrNull((failure as { failureClass?: unknown }).failureClass);
  }
  return textOrNull(result?.failure_class) ?? textOrNull(result?.failureClass);
}

function failureMessageFromResult(result: Record<string, unknown> | null): string | null {
  const failure = result?.failure;
  if (failure && typeof failure === "object" && !Array.isArray(failure)) {
    return textOrNull((failure as { message?: unknown }).message);
  }
  return textOrNull(result?.summary);
}

function firstEventAt(events: readonly PipelineToolInspectorEvent[], kind: PipelineToolEventKind): string | null {
  return events.find((event) => event.kind === kind)?.at ?? null;
}

function durationMs(startedAt: string | null, finishedAt: string | null): number | null {
  if (!startedAt || !finishedAt) return null;
  const start = Date.parse(startedAt);
  const end = Date.parse(finishedAt);
  if (!Number.isFinite(start) || !Number.isFinite(end)) return null;
  return Math.max(0, end - start);
}

function redactActor(actor: string | null | undefined): string | null {
  const text = textOrNull(actor);
  if (!text) return null;
  if (/^claim_[0-9a-f-]{36}$/i.test(text)) return "worker";
  return text;
}

function textOrNull(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed || null;
}
